import * as React from 'react';
import type { AnnotatedJsonSchema } from '@web-config-app/core';
import { isAiAssistEnabled } from '@web-config-app/core';
import { useIntl } from '@leagueplatform/locales';
import { Box } from '@leagueplatform/genesis-core';
import { isUsingConfigAppBackend } from '@web-config-app/common';
import type {
  ControlComponent,
  ControlProps,
} from '@web-config-app/entity-form';
import {
  useEntityDetailsProps,
  EntityFormControl,
} from '@web-config-app/entity-form';
import { JsonLogicInputTemplateSelect } from './json-logic-input-template-select.component';
import { RuleTemplateForm } from './rule-template-form/rule-template-form.component';
import { JsonLogicInputControlWrapper } from './json-logic-input-control-wrapper.component';
import { RuleCombinationControlProvider } from './rule-combination-provider/rule-combination-provider.component';
import { useJsonLogicControl } from './use-json-logic-control';
import { OldJsonLogicInputControlContents } from './old-json-logic-input-control/old-json-logic-input-control.component';
import { defaultRenderers } from '../../../renderers/default-renderers';
import { ruleTemplateJsonLogicInputControlRenderer } from '../../../renderers/rule-template-json-logic-renderer';

/**
 * The rule combination control is the top level json logic control that determines what other json-logic controls should render (a single rule or a combination aka array of rules)
 * The Rule combination control get its data not really from the data prop, but from the ruleTemplateFormData that is returned from useRuleCombinationControl, which in turn gets its data from the Editors data store.
 *
 * However there is a bug right now that on refresh when you are on a page with this component, the data is not loaded from the store right away, so the form is empty. Maybe we need to do something with the data prop on first load?
 */
const RuleCombinationControlContents = ({
  handleChange,
  path,
  enabled,
  schema,
  data,
}: {
  data: any;
  handleChange: (path: string, value: any) => void;
  path: string;
  enabled: boolean;
  schema: AnnotatedJsonSchema;
}) => {
  const { formatMessage } = useIntl();

  const renderers = React.useMemo(
    () => [...defaultRenderers, ruleTemplateJsonLogicInputControlRenderer],
    [],
  );

  const {
    rulesTemplates,
    selectedRuleTemplate,
    ruleTemplateFormData,
    handleRuleTemplateFormDataChange,
    handleTemplateChange,
    jsonLogicFieldPath,
  } = useJsonLogicControl({
    handleChange,
    path,
    schema,
    data,
  });

  const isArraySchema =
    selectedRuleTemplate?.schema?.properties?.ruleCombination.type === 'array';
  const schemaAiAssist =
    schema?.['x-entity-control']?.options?.jsonLogic?.aiAssist;
  const aiAssist =
    enabled && isAiAssistEnabled(schemaAiAssist?.includeIf)
      ? schemaAiAssist
      : undefined;

  return (
    <RuleCombinationControlProvider
      combinationFieldPath={jsonLogicFieldPath}
      isArray={isArraySchema}
      aiAssist={aiAssist}
    >
      <Box
        css={{
          width: '100%',
        }}
      >
        <JsonLogicInputTemplateSelect
          rulesTemplates={rulesTemplates}
          onChange={handleTemplateChange}
          selectedTemplate={selectedRuleTemplate?.id}
          templateLabel={formatMessage({ id: 'COMBINE_RULES_WITH' })}
          id={path}
          disabled={!enabled}
        />
        <RuleTemplateForm
          selectedRuleTemplate={selectedRuleTemplate}
          ruleTemplateData={ruleTemplateFormData}
          onRuleTemplateDataChange={handleRuleTemplateFormDataChange}
          renderers={renderers}
          disabled={!enabled}
        />
      </Box>
    </RuleCombinationControlProvider>
  );
};

/**
 * Main JSON Logic Input Control Renderer for Rule Combination - used to render the Rule Combination control
 */
export const RuleCombinationJsonLogicInputControlRenderer: ControlComponent = (
  props: ControlProps,
) => {
  const { entity, formPath } = useEntityDetailsProps();
  const isConfigAppBE = isUsingConfigAppBackend({
    entityLevelOverride: entity?.isUsingConfigAppBackend,
  });

  const data = props?.data;
  const cacheKeyRef = React.useRef<string | undefined>(formPath);
  const valueRef = React.useRef<string | undefined>(data);

  // Generate a unique key based on formPath and data
  const [key, setKey] = React.useState(() => `${formPath}-${data}`);

  // This is to deal with a bug where when the Rule Combination control is rendered in an array, there is no props.path change when this happens, and not sure if that is the only cause but when switching between rules in arrays the data/values do not update and the component is rendered with stale very stale data.
  // The key forces the component to re-render when the path AND data changes, which is solves the bug when switching between rules in an array.
  // If we just checked for when the path changes, that wouldn't work as the component gets stale data initially - this is why we check for both!
  React.useEffect(() => {
    // Update the key only when both formPath and data are stale
    if (cacheKeyRef.current !== formPath && valueRef.current !== data) {
      cacheKeyRef.current = formPath;
      valueRef.current = data;
      setKey(`${formPath}-${data}`);
    } else {
      valueRef.current = data;
    }
  }, [formPath, data]);

  return (
    <EntityFormControl
      key={key}
      {...props}
      renderControl={
        isConfigAppBE
          ? RuleCombinationControlContents
          : OldJsonLogicInputControlContents
      }
      wrapperComponent={JsonLogicInputControlWrapper}
    />
  );
};
